/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Modal, ModalBody, ModalContent, ModalHeader, ModalTitle, ModalFooter } from "../ui/Modal";
import Button from "../ui/Button";
import Text from "../ui/Text";
import Stack from "../ui/Stack";
import Flex from "../ui/Flex";
import Icon from "../ui/Icon";
import ButtonGroup from "../ui/ButtonGroup";
import BookingStepItem from "../booking/steps/BookingStepItem";
import FormatDatePeriod from "../formatters/FormatDatePeriod";
import { useTranslate } from "../../hooks/useTranslate";
import { useAssetsSrc } from "../../hooks/useAssetsSrc";

const ChoiceInner = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
`;

const ChoiceMedia = styled(Flex)`
  width: 3.625rem;
  height: 3.625rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
  overflow: hidden;
  background: rgba(${({ theme }) => theme.colors.border.rgb}, 0.5);
`;

const ChoiceImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ChoiceSvg = styled.img`
  width: 65%;
  height: 65%;
  object-fit: contain;
`;

const RadioDot = styled.span`
  position: relative;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  border: ${({ $checked }) => ($checked ? "3px" : "2px")} solid rgba(${({ theme }) => theme.colors.border.rgba});

  ${({ $checked, theme }) =>
    $checked &&
    `
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: rgb(${theme.colors.accent.rgb});
    }
  `}
`;

const StyledInfoIcon = styled(Icon.InfoCircle)`
  flex-shrink: 0;
`;

const MembershipChoice = ({ purchase, selected, disabled, tabIndex, onSelect, onKeyDown }) => {
  const { _t } = useTranslate();
  const membership = purchase.membership || {};
  const picture = membership.picture_preview;
  const image = useAssetsSrc(picture);
  const isSvg = !!picture && picture.endsWith(".svg");
  const sessionsLabel = membership.is_unlimited
    ? _t("Unlimited")
    : _t("Sessions remaining: %rest%", { rest: purchase.rest });

  return (
    <BookingStepItem
      selected={selected}
      disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={tabIndex}
      onKeyDown={disabled ? undefined : onKeyDown}
    >
      <ChoiceInner>
        <ChoiceMedia $align="center" $justify="center">
          {picture
            ? isSvg
              ? <ChoiceSvg src={image} alt={membership.name} />
              : <ChoiceImage src={image} alt={membership.name} />
            : <Icon.MembershipItem size={Icon.SIZE.LG} color="iconStroke" />}
        </ChoiceMedia>

        <Stack $gap="0.25rem">
          <Text color="dark" weight="bold" lineClamp={1}>{membership.name}</Text>
          <Flex $gap="0.125rem 0.5rem" $align="center" $wrap="wrap">
            <Flex $gap="0.125rem" $align="center">
              <Icon.FileList size={16} color="secondary" />
              <Text size="small">{sessionsLabel}</Text>
            </Flex>
            {purchase.period_end && (
              <Flex $gap="0.125rem" $align="center">
                <Icon.CalendarEmpty size={16} color="secondary" />
                <Text size="small">
                  {_t("Valid From - To")}:{" "}
                  <FormatDatePeriod
                    from={purchase.period_start}
                    to={purchase.period_end}
                    format={FormatDatePeriod.FORMAT.DATE}
                  />
                </Text>
              </Flex>
            )}
          </Flex>
          {disabled && (
            <Text size="small" color="error">Dates don't overlap your other selected services</Text>
          )}
        </Stack>

        <RadioDot $checked={selected} aria-hidden="true" />
      </ChoiceInner>
    </BookingStepItem>
  );
};

const MembershipChooseModal = ({ isOpen, onClose, memberships = [], disabledIds = new Set(), onConfirm }) => {
  const [selectedId, setSelectedId] = useState(() => memberships.find((m) => !disabledIds.has(m.id))?.id ?? null);
  const groupRef = useRef(null);

  const focusRadioAt = (index) => {
    groupRef.current?.querySelectorAll('[role="radio"]')[index]?.focus();
  };

  // Next enabled option in a direction, wrapping.
  const nextEnabledIndex = (from, dir) => {
    const count = memberships.length;
    let i = from;
    for (let step = 0; step < count; step++) {
      i = (i + dir + count) % count;
      if (!disabledIds.has(memberships[i].id)) return i;
    }
    return from;
  };

  // Standard radiogroup keyboard model: arrows move selection (selection
  // follows focus), Space/Enter selects the focused option.
  const handleKeyDown = (e, index) => {
    if (!memberships.length) return;

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        const next = nextEnabledIndex(index, 1);
        setSelectedId(memberships[next].id);
        focusRadioAt(next);
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        const prev = nextEnabledIndex(index, -1);
        setSelectedId(memberships[prev].id);
        focusRadioAt(prev);
        break;
      }
      case " ":
      case "Enter":
        e.preventDefault();
        if (!disabledIds.has(memberships[index].id)) setSelectedId(memberships[index].id);
        break;
      default:
        break;
    }
  };

  const handleConfirm = () => {
    if (selectedId == null) return;
    onConfirm?.(selectedId);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Choose a membership</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Stack $gap="1rem">
            <Text color="dark">This service is included in several of your memberships. Choose one to continue booking.</Text>
            <Stack ref={groupRef} role="radiogroup" aria-label="Choose a membership" $gap="0.5rem">
              {memberships.map((purchase, index) => (
                <MembershipChoice
                  key={purchase.id}
                  purchase={purchase}
                  selected={purchase.id === selectedId}
                  disabled={disabledIds.has(purchase.id)}
                  tabIndex={purchase.id === selectedId ? 0 : -1}
                  onSelect={() => setSelectedId(purchase.id)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </Stack>
            <Flex $gap="0.125rem" $align="start">
              <StyledInfoIcon size={16} color="secondary" />
              {selectedId == null ? (
                <Text size="small" color="error">
                  None of these memberships cover a date available for your other selected services. Remove a service or choose a different one.
                </Text>
              ) : (
                <Text size="small">One session per participant will be deducted from the selected membership</Text>
              )}
            </Flex>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup itemsWidth="half">
            <Button variant="outline" onClick={onClose} title="Maybe later" />
            <Button variant="primary" onClick={handleConfirm} disabled={selectedId == null} title="Continue" />
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MembershipChooseModal;
